import { useCallback, useEffect, useRef } from "react";

type TimerState<F extends SomeFunction> = {
    timer: ReturnType<typeof setTimeout> | undefined;
    args: Parameters<F> | undefined;
};
type SomeFunction = (...args: any[]) => void;


export function useDebounce<F extends SomeFunction>(func: F, delayMS = 1000) {
    const funcRef = useRef<F>(func);
    const stateRef = useRef<TimerState<F>>({ timer: undefined, args: undefined });

    // Always use the latest func
    funcRef.current = func;

    // Cancel on unmount
    useEffect(() => {
        const state = stateRef.current;
        return () => {
            if (state.timer) clearTimeout(state.timer);
            state.args = undefined;
            state.timer = undefined;
        };
    }, []);

    return useCallback((...args: Parameters<F>) => {
        const state = stateRef.current;

        if (state.timer) clearTimeout(state.timer);
        state.args = args;
        state.timer = setTimeout(() => {
            state.timer = undefined;
            const savedArgs = state.args;
            state.args = undefined;
            if (savedArgs) {
                funcRef.current(...savedArgs);
            }
        }, delayMS);
    }, [delayMS]) as F;
}
